import { useEffect, useState, type ReactNode } from 'react';

/** Avoid building a CSS-hidden view initially; preserve its state once visited. */
export function layoutInitiallyShown(hiddenWhen: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia(hiddenWhen).matches;
}

export function watchUntilShown(media: MediaQueryList, show: () => void): () => void {
  const changed = () => { if (!media.matches) show(); };
  media.addEventListener('change', changed);
  changed();
  return () => media.removeEventListener('change', changed);
}

export function VisibleOnce({ hiddenWhen, children }: { hiddenWhen: string; children: ReactNode }) {
  const [seen, setSeen] = useState(() => layoutInitiallyShown(hiddenWhen));
  useEffect(() => {
    if (seen || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    return watchUntilShown(window.matchMedia(hiddenWhen), () => setSeen(true));
  }, [seen, hiddenWhen]);
  // CSS continues to select the visible layout. Never unmount a visited view:
  // resizing must not discard an open form or a portal owned by that view.
  return seen ? <>{children}</> : null;
}
